import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import { getScreenDimensions } from 'src/lib/utils';
import { ImageScaleView, animate } from '@play-co/timestep-core/ui';
import StateObserver, { user } from 'src/StateObserver';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { Match2Timer } from 'src/game/minigame/match2-timestep/components/Match2Timer';
import { getCurrentGachaEvent } from 'src/replicant/gacha/gacha.getters';
import { gachaTheme } from '../gacha.theme';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import { AssetGroup } from '@play-co/timestep-core';

type CloseHandler = (result: boolean) => void;
const ui = uiConfig.buruburu.care.confirmation;

export default class PopupGachaIntro extends PopupBasicLazy {
  private timer: Match2Timer;

  constructor(opts: { superview: View; close: CloseHandler }) {
    const screen = getScreenDimensions();
    const width = opts.superview.style.width;
    const height = screen.height;

    super({
      superview: opts.superview,
      close: () => opts.close(false),
      ...ui.popup,
      tracking: {
        feature: Feature.GACHA._,
        $subFeature: Feature.GACHA.INTRO,
      },
      width,
      height,
    });

    this.buttonClose.removeFromSuperview();
    this.message.removeFromSuperview();
    this.box.removeFromSuperview();

    // Replace box by an empty view, that also compatible with previous themes
    this.box = new View({
      superview: this.root,
      width,
      height,
      centerAnchor: true,
    });

    const bg = new ImageScaleView({
      superview: this.box,
      width,
      height,
      scaleMethod: 'contain',
      verticalAlign: 'center',
      horizontalAlign: 'center',
      centerAnchor: true,
      ...gachaTheme().getStyle('intro_bg', { width, height }),
    });

    const titleY = height * 0.24;
    const titleX = width / 2;
    const locale = StateObserver.getLocale();

    new ImageScaleView({
      superview: bg,
      x: width / 2,
      y: titleY,
      width: 327,
      height: 147,
      scaleMethod: 'contain',
      verticalAlign: 'center',
      horizontalAlign: 'center',
      centerOnOrigin: true,
      ...gachaTheme().getStyle(`intro_title_${locale}`, { titleY, titleX }),
    });

    new ImageScaleView({
      superview: bg,
      x: width * 0.5,
      y: titleY + 120,
      width: 267,
      height: 87,
      scaleMethod: 'contain',
      verticalAlign: 'center',
      horizontalAlign: 'center',
      centerOnOrigin: true,
      ...gachaTheme().getStyle(`intro_subtitle_${locale}`, { titleY }),
    });

    const timer = new Match2Timer({
      superview: bg,
      centerOnOrigin: true,
      x: width * 0.5,
      y: titleY + 120 + 100,
      ...gachaTheme().getStyle('intro_timer', { titleY }),
    });
    timer.setRemainingTimeGetter(
      (now) => (getCurrentGachaEvent(user(), now)?.end ?? now) - now,
    );
    this.timer = timer;

    new LangBitmapFontTextView({
      superview: bg,
      x: width * 0.5,
      y: height * 0.54,
      width: 550,
      height: 165,
      align: 'center',
      verticalAlign: 'center',
      size: 34,
      color: '#ffffff',
      wordWrap: true,
      font: 'Title',
      localeText: () => i18n('gacha.introText'),
      centerOnOrigin: true,
      ...gachaTheme().getStyle('intro_text', { width, height }),
    });

    new ButtonScaleViewWithText({
      ...ui.okButton,
      superview: bg,
      localeText: () => i18n('basic.continue'),
      onClick: async () => {
        sounds.sfx.play('ok');
        return opts.close(true);
      },
      tracking: {
        feature: Feature.GACHA._,
        $subFeature: Feature.GACHA.INTRO_PLAY,
      },
      x: width / 2,
      y: height * 0.7,
      centerOnOrigin: true,
      ...gachaTheme().getStyle('intro_button', { width, height }),
    });
  }

  public init() {
    super.init({});
  }

  public async fadeIn() {
    this.timer.autoUpdate();
    super.fadeIn();
  }

  public fadeOut(): void {
    this.timer.stopAutoUpdate();
    super.fadeOut();
  }

  protected getAssetsGroup() {
    const urls = gachaTheme().getAssets();
    return new AssetGroup(urls);
  }
}
